import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Headphones, Loader2 } from "lucide-react";
import CallAiAnalysisBlock from "@/components/admin/CallAiAnalysisBlock";

function formatDt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
}

function recordingLoadKey(transactionId, callSessionId) {
  if (callSessionId) return `session:${callSessionId}`;
  return String(transactionId || "");
}

export default function AdminDossierCallHistory({ dossierId, onListenRecording, recordingLoadTid = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);

  useEffect(() => {
    const id = String(dossierId || "").trim();
    if (!id) {
      setHistory(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/admin/dossier-call-history", { params: { dossier_id: id } })
      .then((res) => {
        if (cancelled) return;
        setHistory(res?.data?.data ?? res?.data ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || "Impossible de charger l’historique.");
        setHistory(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  if (!dossierId) return null;

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-xs text-slate-600 flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Historique des contacts prospect…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/80 p-3 text-xs text-red-800">{error}</div>
    );
  }

  const summary = history?.summary;
  const byBuyer = history?.by_buyer || [];
  const sessions = history?.sessions || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3" data-testid="admin-dossier-call-history">
      <div>
        <div className="text-[10px] font-semibold uppercase text-slate-500">
          Contacts prospect — suivi par appel
        </div>
        {summary && summary.total_sessions > 0 ? (
          <p className="mt-1 text-xs text-slate-700">
            <strong>{summary.total_sessions}</strong> tentative{summary.total_sessions !== 1 ? "s" : ""} ·{" "}
            <strong>{summary.distinct_buyers}</strong> acheteur{summary.distinct_buyers !== 1 ? "s" : ""} distinct
            {summary.distinct_buyers !== 1 ? "s" : ""} · prospect composé{" "}
            <strong>{summary.prospect_dialed_sessions}</strong> fois · mise en relation{" "}
            <strong>{summary.bridged_sessions}</strong> fois
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">Aucun appel enregistré sur ce dossier.</p>
        )}
      </div>

      {byBuyer.length > 0 ? (
        <div>
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1.5">Par acheteur</div>
          <ul className="space-y-1.5 text-xs text-slate-700">
            {byBuyer.map((b) => (
              <li key={b.buyer_id} className="rounded border border-slate-200 bg-white px-2 py-1.5">
                <span className="font-medium">{b.name || "—"}</span>
                {b.email ? <span className="text-slate-500"> · {b.email}</span> : null}
                <div className="text-slate-600 mt-0.5">
                  {b.attempt_count} appel{b.attempt_count !== 1 ? "s" : ""}
                  {b.prospect_dialed_count > 0 ? ` · ${b.prospect_dialed_count} composition prospect` : ""}
                  {b.bridged_count > 0 ? ` · ${b.bridged_count} joint` : ""}
                  {b.last_started_at ? ` · dernier : ${formatDt(b.last_started_at)}` : ""}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sessions.length > 0 ? (
        <div>
          <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1.5">Sessions (chronologique)</div>
          <ul className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {sessions.map((s) => {
              const loadKey = recordingLoadKey(s.transaction_id, s.id);
              const isLoading = recordingLoadTid === loadKey;
              return (
                <li
                  key={s.id}
                  className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700"
                >
                  <div className="font-medium">{s.buyer?.name || "Acheteur inconnu"}</div>
                  <div className="text-slate-600">
                    {formatDt(s.started_at)}
                    {s.ended_at ? ` → ${formatDt(s.ended_at)}` : ""}
                  </div>
                  <div className="text-slate-600">
                    Prospect composé : <strong>{s.prospect_dialed ? "oui" : "non"}</strong> · Joint :{" "}
                    <strong>{s.bridged ? "oui" : "non"}</strong>
                  </div>
                  {s.post_call ? (
                    <div className="mt-1 text-slate-700 space-y-0.5">
                      <div>
                        Choix acheteur : <strong>{s.post_call.buyer_choice_label || "—"}</strong>
                      </div>
                      <div>
                        Verdict plateforme :{" "}
                        <strong
                          className={
                            s.post_call.harvest_violation ? "text-amber-800" : "text-slate-900"
                          }
                        >
                          {s.post_call.platform_decision_label || "—"}
                        </strong>
                      </div>
                      {s.post_call.has_ai_analysis && s.post_call.buyer_choice === "refused" ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-0 text-[11px] text-[#2D7FF9] hover:bg-transparent"
                          onClick={() =>
                            setExpandedSessionId((cur) => (cur === s.id ? null : s.id))
                          }
                        >
                          {expandedSessionId === s.id ? "Masquer" : "Voir"} synthèse IA (refus)
                        </Button>
                      ) : s.post_call.decision_pending ? (
                        <span className="text-[11px] text-amber-700">Décision RDV non encore envoyée</span>
                      ) : s.post_call.buyer_choice === "rdv_confirmed" ? (
                        <span className="text-[11px] text-slate-500">Pas d’analyse IA (RDV confirmé)</span>
                      ) : null}
                    </div>
                  ) : null}
                  {expandedSessionId === s.id && s.ai_analysis && s.post_call?.buyer_choice === "refused" ? (
                    <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2">
                      <CallAiAnalysisBlock ia={s.ai_analysis} />
                    </div>
                  ) : null}
                  {onListenRecording && s.recording_available && s.transaction_id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-1.5 h-7 text-[11px]"
                      disabled={isLoading}
                      onClick={() =>
                        onListenRecording(s.transaction_id, { callSessionId: s.id })
                      }
                      data-testid={`admin-dossier-listen-${s.id}`}
                    >
                      <Headphones className="h-3 w-3 mr-1" />
                      {isLoading ? "Chargement…" : "Écouter"}
                    </Button>
                  ) : s.recording_available ? null : (
                    <span className="text-[11px] text-slate-400">Pas d’enregistrement</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
